/**
 * Agent Builder — Telemetry view (Phase 2, T2.5 + run-history).
 *
 * Lists PAST executions from GET /api/agent-runs (durable :AgentRun rows) in a
 * left rail and shows tokens / cost / confidence / guardrail-verdict KPIs + a
 * per-step table for the selected run (GET /api/agent-runs/{id}, whose
 * AgentStepResult rows carry tokens/cost/confidence/model). Survives a refresh
 * (reads Neo4j), and auto-selects the active run when one is started.
 *
 * The cockpit appears ONLY when VITE_ENABLE_AGENT_BUILDER is on (the route is
 * registered behind that flag in App.tsx). Additive: reuses the existing UI
 * primitives + the typed client.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Coins, Gauge, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { agentBuilderApi } from '../../lib/api/agentBuilderApi';
import type { AgentRunRecord, RunEventFrame, RunListItem, RunStatus } from '../../types/agentBuilder';

export interface TelemetryViewProps {
  /** The active run id (set by RunView once a run starts) — auto-selected here. */
  runId?: string | null;
  /** Optional pre-collected SSE frames (guardrail KPIs for the ACTIVE run only). */
  events?: RunEventFrame[];
}

function statusVariant(status: RunStatus) {
  if (status === 'success') return 'success' as const;
  if (status === 'failed' || status === 'rejected') return 'error' as const;
  if (status === 'awaiting_hitl' || status === 'partial') return 'warning' as const;
  return 'neutral' as const;
}

const STATUS_DOT: Record<string, string> = {
  pending: '#94a3b8', running: '#6366f1', success: '#10b981', partial: '#f59e0b',
  failed: '#ef4444', skipped: '#94a3b8', awaiting_hitl: '#f59e0b', rejected: '#ef4444', cancelled: '#94a3b8',
};

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function Kpi({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-neutral-500">
        {icon} {label}
      </div>
      <div className="text-2xl font-semibold text-neutral-800">{value}</div>
      {hint && <div className="text-[11px] text-neutral-400">{hint}</div>}
    </Card>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactElement }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-neutral-100 px-4 py-2.5 text-[12px] font-semibold text-neutral-700">{title}</div>
      <div className="px-2 py-3">
        <ResponsiveContainer width="100%" height={200}>
          {children}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export const TelemetryView: React.FC<TelemetryViewProps> = ({ runId, events = [] }) => {
  const [runs, setRuns] = useState<RunListItem[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(runId ?? null);
  const [run, setRun] = useState<AgentRunRecord | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadList = useCallback(() => {
    setLoadingList(true);
    agentBuilderApi
      .listRuns({ limit: 50 })
      .then((rows) => {
        setRuns(rows);
        setSelectedRunId((cur) => cur ?? runId ?? rows[0]?.runId ?? null);
      })
      .catch((err) => {
        toast.error('Failed to load run history');
        // eslint-disable-next-line no-console
        console.error('listRuns failed', err);
      })
      .finally(() => setLoadingList(false));
  }, [runId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Jump to the active run when one is started/selected upstream.
  useEffect(() => {
    if (runId) setSelectedRunId(runId);
  }, [runId]);

  // Load the selected run's detail.
  useEffect(() => {
    if (!selectedRunId) {
      setRun(null);
      return;
    }
    setLoadingDetail(true);
    agentBuilderApi
      .getRun(selectedRunId)
      .then(setRun)
      .catch((err) => {
        toast.error('Failed to load run telemetry');
        // eslint-disable-next-line no-console
        console.error('getRun failed', err);
      })
      .finally(() => setLoadingDetail(false));
  }, [selectedRunId]);

  // Guardrail verdicts: live SSE frames belong to the ACTIVE run only; for any
  // other selected run fall back to its persisted per-step verdicts.
  const guardrailStats = useMemo(() => {
    let passed = 0;
    let failed = 0;
    const isActive = selectedRunId === runId;
    if (isActive) {
      for (const e of events) {
        if (Array.isArray(e.guardrails)) for (const g of e.guardrails) (g.passed ? passed++ : failed++);
      }
    }
    if (passed === 0 && failed === 0 && run?.steps) {
      for (const s of run.steps) {
        if (Array.isArray(s.guardrails)) for (const g of s.guardrails) (g.passed ? passed++ : failed++);
      }
    }
    return { passed, failed };
  }, [events, run, selectedRunId, runId]);

  const steps = run?.steps ?? [];
  const totalTokens = run?.totalTokens ?? steps.reduce((a, s) => a + (s.tokens || 0), 0);
  const totalCost = run?.totalCostUsd ?? steps.reduce((a, s) => a + (s.costUsd || 0), 0);
  const confidence = run?.overallConfidence ?? 0;

  // Per-step series for the recharts charts (house style).
  const chartData = useMemo(
    () =>
      steps.map((s, i) => ({
        name: (s.agentName || s.nodeId || `#${i + 1}`).slice(0, 12),
        tokens: s.tokens ?? 0,
        confidence: Math.round((s.confidence ?? 0) * 100),
        cost: Number((s.costUsd ?? 0).toFixed(4)),
      })),
    [steps]
  );
  const guardrailPie = useMemo(
    () =>
      [
        { name: 'passed', value: guardrailStats.passed, color: '#10b981' },
        { name: 'failed', value: guardrailStats.failed, color: '#ef4444' },
      ].filter((d) => d.value > 0),
    [guardrailStats]
  );

  return (
    <div className="flex h-full overflow-hidden" data-testid="agent-telemetry-view">
      {/* Left rail: run history */}
      <div className="flex w-[300px] shrink-0 flex-col border-r border-neutral-200 bg-neutral-50">
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-[13px] font-semibold text-neutral-800">Run history</h2>
            <Badge variant="neutral" size="sm">{runs.length}</Badge>
          </div>
          <button onClick={loadList} disabled={loadingList} aria-label="Refresh run history" className="text-neutral-400 hover:text-neutral-700">
            {loadingList ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2.5" data-testid="telemetry-run-list">
          {loadingList && runs.length === 0 && (
            <div className="flex items-center justify-center gap-2 py-10 text-[12px] text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loadingList && runs.length === 0 && (
            <div className="px-3 py-10 text-center text-[12px] text-neutral-400" data-testid="telemetry-empty">
              No runs yet. Start one in the Run tab.
            </div>
          )}
          <ul className="space-y-1.5">
            {runs.map((r) => (
              <li key={r.runId}>
                <button
                  onClick={() => setSelectedRunId(r.runId)}
                  className={`flex w-full flex-col gap-1.5 rounded-lg border px-3 py-2.5 text-left transition-all ${
                    selectedRunId === r.runId
                      ? 'border-secondary-300 bg-secondary-50 ring-1 ring-secondary-200'
                      : 'border-neutral-200 bg-white hover:border-secondary-200 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: STATUS_DOT[r.status] ?? '#94a3b8' }} />
                    <span className="truncate text-[12.5px] font-medium text-neutral-800">
                      {r.task || <span className="font-normal text-neutral-400">(no task)</span>}
                    </span>
                    {r.runId === runId && <Badge variant="info" size="sm">active</Badge>}
                    <Badge variant={statusVariant(r.status)} size="sm" className="ml-auto">{r.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between pl-4 text-[10px] text-neutral-400">
                    <span className="font-mono">{(r.totalTokens ?? 0).toLocaleString()} tok · ${(r.totalCostUsd ?? 0).toFixed(4)}</span>
                    <span>{fmtTime(r.createdAt)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Detail: KPIs + per-step table for the selected run */}
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        {!selectedRunId ? (
          <div className="flex h-full items-center justify-center text-neutral-400">
            Select a run to see its token, cost and guardrail telemetry.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-neutral-800">Run Telemetry</h2>
              <span className="font-mono text-[11px] text-neutral-400">{selectedRunId}</span>
              {run && <Badge variant={statusVariant(run.status)} size="sm">{run.status}</Badge>}
              {loadingDetail && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="telemetry-kpis">
              <Kpi icon={<Coins className="h-3.5 w-3.5" />} label="Tokens" value={totalTokens.toLocaleString()} />
              <Kpi icon={<Coins className="h-3.5 w-3.5" />} label="Cost (USD)" value={`$${totalCost.toFixed(4)}`} />
              <Kpi icon={<Gauge className="h-3.5 w-3.5" />} label="Confidence" value={`${Math.round(confidence * 100)}%`} />
              <Kpi
                icon={guardrailStats.failed > 0 ? <ShieldAlert className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                label="Guardrails"
                value={
                  guardrailStats.passed + guardrailStats.failed > 0
                    ? `${Math.round((guardrailStats.passed / (guardrailStats.passed + guardrailStats.failed)) * 100)}% passed`
                    : '—'
                }
                hint={`${guardrailStats.passed}/${guardrailStats.passed + guardrailStats.failed} checks`}
              />
            </div>

            {/* Charts */}
            {steps.length > 0 && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="telemetry-charts">
                <ChartCard title="Tokens per step">
                  <BarChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} dy={6} height={36} />
                    <YAxis tick={{ fontSize: 11 }} width={40} />
                    <Tooltip wrapperStyle={{ fontSize: 11 }} cursor={{ fill: '#f9fafb' }} />
                    <Bar dataKey="tokens" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartCard>
                <ChartCard title="Confidence per step (%)">
                  <BarChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} dy={6} height={36} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={40} />
                    <Tooltip wrapperStyle={{ fontSize: 11 }} cursor={{ fill: '#f9fafb' }} />
                    <Bar dataKey="confidence" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartCard>
                <ChartCard title="Cost per step (USD)">
                  <BarChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} dy={6} height={36} />
                    <YAxis tick={{ fontSize: 11 }} width={48} />
                    <Tooltip
                      wrapperStyle={{ fontSize: 11 }}
                      cursor={{ fill: '#f9fafb' }}
                      formatter={(v: number) => [`$${Number(v).toFixed(4)}`, 'cost']}
                    />
                    <Bar dataKey="cost" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartCard>
                {guardrailPie.length > 0 && (
                  <ChartCard title="Guardrail outcomes">
                    <PieChart>
                      <Pie data={guardrailPie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3} stroke="none">
                        {guardrailPie.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ChartCard>
                )}
              </div>
            )}

            {/* Per-step breakdown */}
            <Card className="overflow-hidden">
              <table className="w-full text-left text-[12px]" data-testid="telemetry-step-table">
                <thead className="bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="px-3 py-2">Node</th>
                    <th className="px-3 py-2">Agent</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Iter</th>
                    <th className="px-3 py-2 text-right">Conf</th>
                    <th className="px-3 py-2 text-right">Tokens</th>
                    <th className="px-3 py-2 text-right">Cost</th>
                    <th className="px-3 py-2">Model</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-neutral-400">
                        {loadingDetail ? 'Loading…' : 'No step results recorded yet.'}
                      </td>
                    </tr>
                  )}
                  {steps.map((s) => (
                    <tr key={`${s.nodeId}-${s.iteration}-${s.attempt}`} className="border-t border-neutral-100">
                      <td className="px-3 py-2 font-mono text-[11px]">{s.nodeId}</td>
                      <td className="px-3 py-2">{s.agentName}</td>
                      <td className="px-3 py-2">
                        <Badge variant={statusVariant(s.status)} size="sm">{s.status}</Badge>
                      </td>
                      <td className="px-3 py-2">{s.iteration}</td>
                      <td className="px-3 py-2 text-right font-mono">{(s.confidence ?? 0) > 0 ? (s.confidence ?? 0).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{(s.tokens ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">${(s.costUsd ?? 0).toFixed(4)}</td>
                      <td className="px-3 py-2 text-[11px] text-neutral-500">{s.model || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TelemetryView;
